import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Trash2,
  Save,
  Download,
  Eye,
  Layers,
  Plus,
  Minus,
  Calendar,
  Package,
  ChevronLeft,
  ChevronRight,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StickerPreview from "@/components/StickerPreview";
import { listPresets, createPreset, deletePreset, generatePdf } from "@/lib/api";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function WorkspacePage({ companies }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const company = useMemo(
    () => companies.find((c) => c.id === companyId),
    [companies, companyId]
  );

  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [description, setDescription] = useState("");
  const [boxes, setBoxes] = useState(1);
  const [date, setDate] = useState(todayISO());

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activePresetId, setActivePresetId] = useState(null);
  const [previewBoxIndex, setPreviewBoxIndex] = useState(1);

  useEffect(() => {
    if (companies.length > 0 && !company) {
      toast.error("Company not found");
      navigate("/");
    }
  }, [companies, company, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  const refreshPresets = useCallback(async () => {
    if (!companyId) return;
    setPresetsLoading(true);
    try {
      const data = await listPresets(companyId, debouncedSearch);
      setPresets(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load presets");
    } finally {
      setPresetsLoading(false);
    }
  }, [companyId, debouncedSearch]);

  useEffect(() => {
    refreshPresets();
  }, [refreshPresets]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Keep preview box index within range when boxes changes
  useEffect(() => {
    setPreviewBoxIndex((idx) => Math.min(Math.max(1, idx), Math.max(1, boxes)));
  }, [boxes]);

  const loadPreset = (p) => {
    setItemName(p.item_name);
    setQuantity(p.quantity);
    setDescription(p.description || "");
    setActivePresetId(p.id);
  };

  const handleSavePreset = async () => {
    if (!itemName.trim() || !quantity.trim()) {
      toast.error("Item name and quantity are required to save a preset");
      return;
    }
    setSaving(true);
    try {
      const created = await createPreset(companyId, {
        item_name: itemName.trim(),
        quantity: quantity.trim(),
        description: description.trim(),
      });
      toast.success("Preset saved");
      setActivePresetId(created.id);
      await refreshPresets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save preset");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async (p) => {
    try {
      await deletePreset(p.id);
      toast.success("Preset deleted");
      if (activePresetId === p.id) setActivePresetId(null);
      await refreshPresets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete preset");
    }
  };

  const handleGenerate = async (mode) => {
    if (!itemName.trim() || !quantity.trim()) {
      toast.error("Enter item name and quantity first");
      return;
    }
    setGenerating(true);
    try {
      const blob = await generatePdf({
        company_id: companyId,
        item_name: itemName.trim(),
        quantity: quantity.trim(),
        description: description.trim(),
        boxes: Number(boxes) || 1,
        date: date || null,
      });
      const url = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });

      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        const safeItem =
          itemName.replace(/[^a-z0-9\-_ ]/gi, "").trim().replace(/\s+/g, "_") || "sticker";
        const safeCompany = (company?.name || "company")
          .replace(/[^a-z0-9\-_ ]/gi, "")
          .trim()
          .replace(/\s+/g, "_");
        const suffix = boxes > 1 ? `_x${boxes}` : "";
        a.download = `${safeCompany}_${safeItem}_A6${suffix}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(boxes > 1 ? `PDF downloaded (${boxes} pages)` : "PDF downloaded");
      } else if (mode === "print") {
        // Open the PDF inside a hidden iframe and ask the browser to print.
        // This is the most reliable cross-browser pattern: Chromium and
        // Firefox print the PDF directly; Safari falls back to opening the
        // PDF in a new tab where the user can hit ⌘P.
        const iframe = document.createElement("iframe");
        iframe.style.cssText =
          "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
        iframe.src = url;
        document.body.appendChild(iframe);
        let printed = false;
        const triggerPrint = () => {
          if (printed) return;
          printed = true;
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            toast.success("Print dialog opened");
            // Clean up later — keep iframe around long enough for the
            // browser to consume the print job.
            setTimeout(() => iframe.remove(), 60_000);
          } catch (e) {
            // Safari + some popup-blocker situations end up here.
            iframe.remove();
            const w = window.open(url, "_blank");
            if (w) {
              toast.message("Opened in a new tab — press Ctrl/⌘+P to print");
            } else {
              toast.error("Couldn't open print dialog. Please use Download PDF and print from there.");
            }
          }
        };
        iframe.onload = triggerPrint;
        // Hard fallback in case onload never fires (some PDF viewers).
        setTimeout(triggerPrint, 1500);
      } else {
        toast.success("Preview updated");
      }
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const adjustBoxes = (delta) => {
    setBoxes((b) => {
      const next = Math.max(1, Math.min(500, (Number(b) || 1) + delta));
      return next;
    });
  };

  if (!company) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 fade-up">
      {/* Left: Presets */}
      <aside className="lg:col-span-3">
        <div className="sticky top-20">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
              <Layers className="h-4 w-4" /> Presets
            </h2>
            <span className="text-xs text-neutral-500">{presets.length}</span>
          </div>
          <div className="relative mb-3">
            <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="preset-search-input"
              placeholder="Search presets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="space-y-2 max-h-[68vh] overflow-y-auto scroll-area pr-1">
            {presetsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 rounded-md border border-neutral-200 bg-neutral-50 animate-pulse"
                />
              ))
            ) : presets.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-300 p-6 text-center">
                <p className="text-sm text-neutral-600 font-medium">No presets yet</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Fill the generator and click Save preset.
                </p>
              </div>
            ) : (
              presets.map((p) => (
                <div
                  key={p.id}
                  data-testid={`preset-card-${p.id}`}
                  className={`group rounded-md border p-3 cursor-pointer transition-all bg-white ${
                    activePresetId === p.id
                      ? "border-neutral-900 shadow-sm"
                      : "border-neutral-200 hover:border-neutral-300"
                  }`}
                  onClick={() => loadPreset(p)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div
                        className="font-semibold text-sm text-neutral-900 truncate"
                        title={p.item_name}
                      >
                        {p.item_name}
                      </div>
                      <div className="text-xs text-neutral-700 mt-0.5 font-medium">
                        {p.quantity}
                      </div>
                      {p.description && (
                        <div
                          className="text-xs text-neutral-500 mt-1 line-clamp-1"
                          title={p.description}
                        >
                          {p.description}
                        </div>
                      )}
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-neutral-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-preset-${p.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete preset?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{p.item_name}" will be removed permanently.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeletePreset(p)}
                            data-testid={`confirm-delete-preset-${p.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Middle: Form */}
      <section className="lg:col-span-5">
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Generator</h2>
              <p className="text-xs text-neutral-500">
                Compose an A6 landscape sticker for {company.name}.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setItemName("");
                setQuantity("");
                setDescription("");
                setBoxes(1);
                setDate(todayISO());
                setActivePresetId(null);
              }}
              data-testid="clear-form-button"
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> New
            </Button>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="item-name">Item name</Label>
              <Input
                id="item-name"
                data-testid="item-name-input"
                placeholder="e.g. Outer Packaging - Phone"
                value={itemName}
                maxLength={200}
                onChange={(e) => setItemName(e.target.value)}
              />
              <p className="text-[11px] text-neutral-500">
                Printed in huge bold type. Wraps up to 3 lines.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity (per box)</Label>
              <Input
                id="quantity"
                data-testid="quantity-input"
                placeholder="e.g. 500 pcs"
                value={quantity}
                maxLength={80}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <p className="text-[11px] text-neutral-500">
                Big number on the bottom-right. Unit (pcs, kg, etc.) renders smaller next to it.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="boxes" className="flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" /> Boxes
                </Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => adjustBoxes(-1)}
                    disabled={boxes <= 1}
                    data-testid="boxes-decrement"
                    aria-label="Decrease boxes"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="boxes"
                    type="number"
                    min={1}
                    max={500}
                    value={boxes}
                    data-testid="boxes-input"
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isFinite(v)) setBoxes(Math.max(1, Math.min(500, v)));
                      else setBoxes(1);
                    }}
                    className="text-center"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => adjustBoxes(1)}
                    disabled={boxes >= 500}
                    data-testid="boxes-increment"
                    aria-label="Increase boxes"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-neutral-500">
                  {boxes > 1
                    ? `Generates ${boxes} pages: 1/${boxes}, 2/${boxes}, ..., ${boxes}/${boxes}.`
                    : "Single sticker. No box indicator shown."}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Date
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  data-testid="date-input"
                  onChange={(e) => setDate(e.target.value)}
                />
                <p className="text-[11px] text-neutral-500">Shown in the top-right corner.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                placeholder="e.g. Stock for Q1 2026 production batch."
                value={description}
                maxLength={500}
                rows={2}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="flex justify-end">
                <span className="text-[11px] text-neutral-400">{description.length}/500</span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => handleGenerate("preview")}
              disabled={generating}
              variant="outline"
              data-testid="preview-button"
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              {generating ? "Generating..." : "Update PDF preview"}
            </Button>
            <Button
              onClick={() => handleGenerate("download")}
              disabled={generating}
              data-testid="download-button"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download PDF{boxes > 1 ? ` (${boxes} pages)` : ""}
            </Button>
            <Button
              onClick={() => handleGenerate("print")}
              disabled={generating}
              variant="outline"
              data-testid="print-button"
              className="gap-2"
              title="Open the system print dialog with this sticker"
            >
              <Printer className="h-4 w-4" />
              Print{boxes > 1 ? ` (${boxes} pages)` : ""}
            </Button>
            <div className="flex-1" />
            <Button
              variant="secondary"
              onClick={handleSavePreset}
              disabled={saving}
              data-testid="save-preset-button"
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save preset"}
            </Button>
          </div>
        </div>
      </section>

      {/* Right: Preview */}
      <section className="lg:col-span-4">
        <div className="sticky top-20 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-900">Live preview</h2>
            <span className="text-[11px] text-neutral-500">A6 landscape · 148 × 105 mm</span>
          </div>

          <StickerPreview
            company={company}
            itemName={itemName}
            quantity={quantity}
            description={description}
            boxes={boxes}
            boxIndex={previewBoxIndex}
            date={date}
          />

          {boxes > 1 && (
            <div className="flex items-center justify-between bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setPreviewBoxIndex((i) => Math.max(1, i - 1))}
                disabled={previewBoxIndex <= 1}
                data-testid="preview-prev-page"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-neutral-600">
                Previewing page <span className="font-semibold text-neutral-900">{previewBoxIndex}</span> of {boxes}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setPreviewBoxIndex((i) => Math.min(boxes, i + 1))}
                disabled={previewBoxIndex >= boxes}
                data-testid="preview-next-page"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {pdfUrl && (
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200">
                <span className="text-xs font-medium text-neutral-700">
                  PDF preview{boxes > 1 ? ` · ${boxes} pages` : ""}
                </span>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-neutral-700 hover:text-neutral-900 underline"
                  data-testid="open-pdf-link"
                >
                  Open in new tab
                </a>
              </div>
              <iframe
                title="PDF preview"
                src={pdfUrl}
                className="w-full h-72 bg-neutral-50"
                data-testid="pdf-iframe"
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
